// Admin session + admin mutations. v2 stores NO admin secret client-side: admin is
// either the session role (admin access code) or the HttpOnly dt_admin remember-cookie,
// silently re-verified on boot (V1_BEHAVIOR §1 paths 3/4 — the raw-password header path
// still works server-side but the client never needs it).
import { computed, signal } from '@preact/signals';
import { ApiError, apiPost } from '../lib/api';
import type { WeekView } from './schedule';
import { applyWeek, loadWeeks, pickWeek, week } from './schedule';
import { refreshCompletions } from './completions';
import { role } from './session';
import { toast } from './toast';

/** True when the dt_admin cookie verified on a tech-code session. */
const cookieAdmin = signal(false);
export const adminActive = computed(() => role.value === 'admin' || cookieAdmin.value);

/**
 * Boot-time restore of a remembered admin unlock. Raw fetch on purpose: a 401 here is
 * the NORMAL tech answer and must not trip the app-wide auth gate.
 */
export async function restoreAdmin(): Promise<void> {
  if (role.value === 'admin') return;
  try {
    const res = await fetch('/api/admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'verify' }),
    });
    if (res.ok) cookieAdmin.value = true; // verify also renewed the rolling cookie
  } catch {
    // offline — stay tech; the next boot retries
  }
}

/** v1 signOutDevice: drop the admin cookie, then the whole session, then restart. */
export async function signOutDevice(): Promise<void> {
  await apiPost('/api/admin', { action: 'lock' }).catch(() => {});
  await fetch('/api/logout', { method: 'POST' }).catch(() => {});
  location.reload();
}

function failToast(e: unknown, what: string): void {
  if (e instanceof ApiError && e.status === 401) toast('Admin session expired — sign in again.');
  else toast(`${what} failed.`);
}

/**
 * One admin schedule mutation. The server echoes the fresh week view of the TARGET
 * week; when that's the week on the board it replaces it (v1's afterDbMutation rule).
 */
export async function schedAction(body: Record<string, unknown>): Promise<WeekView | null> {
  try {
    const w = await apiPost<WeekView>('/api/schedule', body, {
      idempotencyKey: crypto.randomUUID(),
    });
    if (w.weekId && week.value?.weekId === w.weekId) applyWeek(w);
    return w;
  } catch (e) {
    failToast(e, 'Save');
    return null;
  }
}

export async function createWeek(weekOf: string): Promise<WeekView | null> {
  const w = await schedAction({ action: 'createWeek', weekOf });
  if (w) await loadWeeks().catch(() => {});
  return w;
}

/** Jump to the week containing today; an admin creates it on the spot when missing. */
export async function goToCurrentWeek(monday: string, existing: string | undefined): Promise<void> {
  if (existing) {
    pickWeek(existing);
    return;
  }
  const w = await createWeek(monday);
  if (w) {
    toast('Current week ready.');
    pickWeek(w.weekId);
  }
}

/** Admin reopen of ANY completion (v1 /api/admin reopen — audited, tombstone-safe). */
export async function adminReopen(id: string): Promise<boolean> {
  try {
    await apiPost('/api/admin', { action: 'reopen', id });
    await refreshCompletions().catch(() => {});
    return true;
  } catch (e) {
    failToast(e, 'Reopen');
    return false;
  }
}

// ---- parked (gray-out) ----
import { parked } from './completions';

export async function setParked(id: string, on: boolean): Promise<void> {
  // optimistic — the whole board grays instantly
  const next = new Set(parked.value);
  if (on) next.add(id);
  else next.delete(id);
  parked.value = next;
  try {
    const res = await apiPost<{ ids: Record<string, boolean> }>('/api/parked', {
      action: on ? 'set' : 'clear',
      id,
    });
    parked.value = new Set(Object.keys(res.ids));
  } catch (e) {
    failToast(e, 'Update');
    await refreshCompletions().catch(() => {}); // revert to server truth
  }
}
